import { useState, useEffect, useCallback, useRef } from 'react';
import { cashVouchersAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Plus, Banknote, CheckCircle2, XCircle, Clock, Filter, Upload,
  Loader2, ChevronLeft, Trash2, Edit2, Eye, Receipt, BarChart3,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';

const CATEGORIES = [
  { value: 'courier', label: 'Courier', color: '#2563EB' },
  { value: 'supplies', label: 'Supplies', color: '#059669' },
  { value: 'transport', label: 'Transport', color: '#D97706' },
  { value: 'food', label: 'Food', color: '#DC2626' },
  { value: 'vendor_payment', label: 'Vendor Payment', color: '#7C3AED' },
  { value: 'worker_advance', label: 'Worker Advance', color: '#0891B2' },
  { value: 'miscellaneous', label: 'Miscellaneous', color: '#6B7280' },
  { value: 'other', label: 'Other', color: '#64748B' },
];

const STATUS_CFG = {
  pending_approval: { label: 'Pending', color: '#D97706', bg: 'rgba(245,158,11,0.08)', icon: Clock },
  approved: { label: 'Approved', color: '#059669', bg: 'rgba(16,185,129,0.08)', icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: '#DC2626', bg: 'rgba(239,68,68,0.08)', icon: XCircle },
};

const getCategoryConfig = (cat) => CATEGORIES.find(c => c.value === cat) || { label: cat, color: '#6B7280' };

/* ========== New Voucher Form ========== */
function NewVoucherForm({ onCreated, onCancel }) {
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: '',
    paid_to: '',
    purpose: '',
    category: 'miscellaneous',
    notes: '',
  });
  const [receiptFile, setReceiptFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.amount || !form.paid_to || !form.purpose) {
      toast.error('Amount, Paid To, and Purpose are required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await cashVouchersAPI.create({
        ...form,
        amount: parseFloat(form.amount),
      });
      
      // Upload receipt if provided
      if (receiptFile && res.data?.id) {
        const fd = new FormData();
        fd.append('file', receiptFile);
        await cashVouchersAPI.uploadReceipt(res.data.id, fd);
      }
      
      toast.success(`Voucher ${res.data.voucher_number} created`);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create voucher');
    }
    setSubmitting(false);
  };

  return (
    <Card className="rounded-xl border bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]" data-testid="new-voucher-form">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
          <Banknote className="w-5 h-5" style={{ color: 'var(--brand-gold)' }} /> New Cash Voucher
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs">Date *</Label>
              <Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})}
                className="mt-1 h-9" data-testid="voucher-date" />
            </div>
            <div>
              <Label className="text-xs">Amount (₹) *</Label>
              <Input type="number" step="0.01" min="0" value={form.amount}
                onChange={e => setForm({...form, amount: e.target.value})}
                placeholder="0.00" className="mt-1 h-9" data-testid="voucher-amount" />
            </div>
            <div>
              <Label className="text-xs">Category *</Label>
              <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                <SelectTrigger className="mt-1 h-9" data-testid="voucher-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Paid To *</Label>
              <Input value={form.paid_to} onChange={e => setForm({...form, paid_to: e.target.value})}
                placeholder="Name of person/vendor who received cash" className="mt-1 h-9" data-testid="voucher-paid-to" />
            </div>
            <div>
              <Label className="text-xs">Purpose *</Label>
              <Input value={form.purpose} onChange={e => setForm({...form, purpose: e.target.value})}
                placeholder="What is the expense for?" className="mt-1 h-9" data-testid="voucher-purpose" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
                placeholder="Additional details..." rows={2} className="mt-1" data-testid="voucher-notes" />
            </div>
            <div>
              <Label className="text-xs">Receipt Photo (optional)</Label>
              <div className="mt-1 flex items-center gap-2">
                <Input type="file" ref={fileRef} accept=".jpg,.jpeg,.png,.webp,.pdf"
                  onChange={e => setReceiptFile(e.target.files?.[0] || null)}
                  className="h-9 text-xs flex-1" data-testid="voucher-receipt" />
                {receiptFile && <Badge variant="outline" className="text-[10px]">{receiptFile.name}</Badge>}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onCancel} data-testid="cancel-voucher-btn">Cancel</Button>
            <Button type="submit" disabled={submitting} className="gap-1.5"
              style={{ backgroundColor: 'var(--brand-navy)' }} data-testid="submit-voucher-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Create Voucher
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

/* ========== Voucher Detail Modal ========== */
function VoucherDetailModal({ voucher, onClose, onUpdated, canApprove }) {
  const [approving, setApproving] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');
  const [showReject, setShowReject] = useState(false);

  const handleApprove = async () => {
    setApproving(true);
    try {
      await cashVouchersAPI.review(voucher.id, { action: 'approve' });
      toast.success('Voucher approved');
      onUpdated();
    } catch { toast.error('Failed'); }
    setApproving(false);
  };

  const handleReject = async () => {
    setApproving(true);
    try {
      await cashVouchersAPI.review(voucher.id, { action: 'reject', notes: rejectNotes });
      toast.success('Voucher rejected');
      onUpdated();
    } catch { toast.error('Failed'); }
    setApproving(false);
  };

  const sc = STATUS_CFG[voucher.status] || STATUS_CFG.pending_approval;
  const catCfg = getCategoryConfig(voucher.category);

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="voucher-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
            <Receipt className="w-5 h-5" style={{ color: 'var(--brand-gold)' }} />
            {voucher.voucher_number}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Badge variant="outline" className="text-[10px] px-2 py-1 font-semibold"
              style={{ backgroundColor: sc.bg, color: sc.color }}>{sc.label}</Badge>
            <Badge variant="outline" className="text-[10px] px-2 py-1"
              style={{ borderColor: catCfg.color + '40', color: catCfg.color }}>{catCfg.label}</Badge>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: 'var(--ink-muted)' }}>Amount</p>
              <p className="text-xl font-bold" style={{ color: 'var(--brand-navy)' }}>₹{voucher.amount.toLocaleString('en-IN')}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: 'var(--ink-muted)' }}>Date</p>
              <p className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>{voucher.date}</p>
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: 'var(--ink-muted)' }}>Paid To</p>
            <p className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>{voucher.paid_to}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: 'var(--ink-muted)' }}>Purpose</p>
            <p className="text-sm" style={{ color: 'var(--navy-ink)' }}>{voucher.purpose}</p>
          </div>
          {voucher.notes && (
            <div>
              <p className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: 'var(--ink-muted)' }}>Notes</p>
              <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>{voucher.notes}</p>
            </div>
          )}
          {voucher.receipt_image_url && (
            <div>
              <p className="text-[10px] uppercase tracking-wide font-semibold mb-1" style={{ color: 'var(--ink-muted)' }}>Receipt</p>
              <a href={voucher.receipt_image_url} target="_blank" rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                <Eye className="w-3 h-3" /> View Receipt
              </a>
            </div>
          )}
          <div className="pt-2 border-t" style={{ borderColor: 'rgba(27,42,74,0.08)' }}>
            <p className="text-[9px]" style={{ color: 'var(--ink-muted)' }}>
              Created by {voucher.created_by_name} on {new Date(voucher.created_at).toLocaleString()}
            </p>
            {voucher.approved_by_name && (
              <p className="text-[9px]" style={{ color: voucher.status === 'approved' ? '#059669' : '#DC2626' }}>
                {voucher.status === 'approved' ? 'Approved' : 'Rejected'} by {voucher.approved_by_name} on {voucher.approval_date}
              </p>
            )}
            {voucher.rejection_notes && (
              <p className="text-[9px] mt-1" style={{ color: '#DC2626' }}>Reason: {voucher.rejection_notes}</p>
            )}
          </div>
        </div>
        
        {/* Approval Actions (Admin only, pending only) */}
        {canApprove && voucher.status === 'pending_approval' && (
          <div className="pt-3 border-t mt-2" style={{ borderColor: 'rgba(27,42,74,0.08)' }}>
            {!showReject ? (
              <div className="flex gap-2">
                <Button className="flex-1 gap-1.5" style={{ backgroundColor: '#059669' }}
                  onClick={handleApprove} disabled={approving} data-testid="approve-voucher-btn">
                  <CheckCircle2 className="w-4 h-4" /> Approve
                </Button>
                <Button variant="outline" className="flex-1 gap-1.5 text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => setShowReject(true)} data-testid="reject-voucher-show">
                  <XCircle className="w-4 h-4" /> Reject
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <Label className="text-xs">Rejection Reason</Label>
                <Textarea value={rejectNotes} onChange={e => setRejectNotes(e.target.value)}
                  placeholder="Why is this voucher being rejected?" rows={2} data-testid="reject-notes" />
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowReject(false)} className="flex-1">Cancel</Button>
                  <Button style={{ backgroundColor: '#DC2626' }} onClick={handleReject}
                    disabled={approving} className="flex-1 gap-1" data-testid="confirm-reject-btn">
                    <XCircle className="w-4 h-4" /> Confirm Reject
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ========== Monthly Summary Chart ========== */
function MonthlySummaryChart({ data }) {
  if (!data || !data.by_category || data.by_category.length === 0) {
    return (
      <Card className="rounded-xl border bg-white">
        <CardContent className="py-8 text-center">
          <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>No approved vouchers this month</p>
        </CardContent>
      </Card>
    );
  }

  const chartData = data.by_category.map(item => ({
    name: getCategoryConfig(item.category).label,
    amount: item.amount,
    color: getCategoryConfig(item.category).color,
  }));

  return (
    <Card className="rounded-xl border bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]" data-testid="monthly-summary-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center justify-between" style={{ color: 'var(--brand-navy)' }}>
          <span className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" style={{ color: 'var(--brand-gold)' }} />
            {data.month_name} Summary
          </span>
          <span className="text-lg font-bold" style={{ color: '#DC2626' }}>₹{data.total_approved.toLocaleString('en-IN')}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-2">
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData} layout="vertical" barGap={4}>
            <XAxis type="number" tick={{ fontSize: 10, fill: '#6B7280' }} axisLine={false} tickLine={false}
              tickFormatter={v => `₹${(v/1000).toFixed(v >= 1000 ? 1 : 0)}${v >= 1000 ? 'k' : ''}`} />
            <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: '#6B7280' }} axisLine={false} tickLine={false} width={90} />
            <Tooltip
              formatter={(value) => [`₹${value.toLocaleString('en-IN')}`, 'Amount']}
              contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e5e7eb' }}
            />
            <Bar dataKey="amount" radius={[0, 4, 4, 0]}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <p className="text-[10px] text-center mt-2" style={{ color: 'var(--ink-muted)' }}>
          {data.voucher_count} approved voucher{data.voucher_count !== 1 ? 's' : ''}
        </p>
      </CardContent>
    </Card>
  );
}

/* ========== Main Page ========== */
export default function CashVouchersPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin';
  
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [monthlySummary, setMonthlySummary] = useState(null);
  
  // Filters
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  const fetchVouchers = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterCategory) params.category = filterCategory;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      
      const res = await cashVouchersAPI.list(params);
      setVouchers(res.data || []);
    } catch (err) {
      console.error('Failed to fetch vouchers', err);
    }
    setLoading(false);
  }, [filterStatus, filterCategory, filterDateFrom, filterDateTo]);

  const fetchSummary = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await cashVouchersAPI.monthlySummary({});
      setMonthlySummary(res.data);
    } catch (err) {
      console.error('Failed to fetch summary', err);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchVouchers();
    fetchSummary();
  }, [fetchVouchers, fetchSummary]);

  const handleCreated = () => {
    setShowNew(false);
    fetchVouchers();
    fetchSummary();
  };

  const handleUpdated = () => {
    setSelectedVoucher(null);
    fetchVouchers();
    fetchSummary();
  };

  const handleDelete = async (voucher) => {
    if (!confirm(`Delete voucher ${voucher.voucher_number}?`)) return;
    try {
      await cashVouchersAPI.delete(voucher.id);
      toast.success('Voucher deleted');
      fetchVouchers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const clearFilters = () => {
    setFilterStatus('');
    setFilterCategory('');
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  const pendingVouchers = vouchers.filter(v => v.status === 'pending_approval');
  const displayVouchers = activeTab === 'pending' ? pendingVouchers : vouchers;

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-2 gap-4"><Skeleton className="h-24" /><Skeleton className="h-24" /></div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cash-vouchers-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-semibold" style={{ color: 'var(--brand-navy)' }}>Cash Vouchers</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--ink-muted)' }}>Track petty cash expenses and reimbursements</p>
        </div>
        <Button onClick={() => setShowNew(true)} className="gap-1.5"
          style={{ backgroundColor: 'var(--brand-navy)' }} data-testid="new-voucher-btn">
          <Plus className="w-4 h-4" /> New Voucher
        </Button>
      </div>

      {/* New Voucher Form */}
      {showNew && (
        <NewVoucherForm onCreated={handleCreated} onCancel={() => setShowNew(false)} />
      )}

      {/* Tabs for Admin: All / Pending Approval */}
      {isAdmin && (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-fit grid-cols-2 h-9">
            <TabsTrigger value="all" className="text-xs px-4" data-testid="tab-all">
              All Vouchers
            </TabsTrigger>
            <TabsTrigger value="pending" className="text-xs px-4 gap-1" data-testid="tab-pending">
              Pending Approval
              {pendingVouchers.length > 0 && (
                <Badge className="text-[9px] px-1.5 ml-1" style={{ backgroundColor: '#D97706' }}>
                  {pendingVouchers.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {/* Stats + Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Quick Stats */}
        <Card className="rounded-xl border bg-white">
          <CardContent className="p-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center">
                <p className="text-2xl font-bold" style={{ color: '#D97706' }}>{pendingVouchers.length}</p>
                <p className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: 'var(--ink-muted)' }}>Pending</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold" style={{ color: '#059669' }}>
                  {vouchers.filter(v => v.status === 'approved').length}
                </p>
                <p className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: 'var(--ink-muted)' }}>Approved</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold" style={{ color: '#DC2626' }}>
                  {vouchers.filter(v => v.status === 'rejected').length}
                </p>
                <p className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: 'var(--ink-muted)' }}>Rejected</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Monthly Summary Chart (Admin only) */}
        {isAdmin && monthlySummary && (
          <div className="lg:col-span-2">
            <MonthlySummaryChart data={monthlySummary} />
          </div>
        )}
      </div>

      {/* Filters */}
      <Card className="rounded-xl border bg-white">
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center gap-3">
            <Filter className="w-4 h-4" style={{ color: 'var(--ink-muted)' }} />
            <Select value={filterStatus || 'all'} onValueChange={v => setFilterStatus(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="filter-status">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending_approval">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterCategory || 'all'} onValueChange={v => setFilterCategory(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="filter-category">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map(c => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1">
              <Input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)}
                className="h-8 text-xs w-[130px]" placeholder="From" data-testid="filter-date-from" />
              <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>to</span>
              <Input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)}
                className="h-8 text-xs w-[130px]" placeholder="To" data-testid="filter-date-to" />
            </div>
            {(filterStatus || filterCategory || filterDateFrom || filterDateTo) && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8 text-xs"
                data-testid="clear-filters">Clear</Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Vouchers Table */}
      <Card className="rounded-xl border bg-white overflow-hidden shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="vouchers-table">
            <thead>
              <tr style={{ backgroundColor: 'rgba(27,42,74,0.02)', borderBottom: '1px solid rgba(27,42,74,0.08)' }}>
                {['Voucher #', 'Date', 'Amount', 'Paid To', 'Purpose', 'Category', 'Status', 'Actions'].map(h => (
                  <th key={h} className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wide"
                    style={{ color: 'var(--ink-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayVouchers.map(v => {
                const sc = STATUS_CFG[v.status] || STATUS_CFG.pending_approval;
                const catCfg = getCategoryConfig(v.category);
                const StatusIcon = sc.icon;
                
                return (
                  <tr key={v.id} className="border-b hover:bg-gray-50/50"
                    style={{ borderColor: 'rgba(27,42,74,0.04)' }}
                    data-testid={`voucher-row-${v.id}`}>
                    <td className="px-4 py-2.5 font-semibold" style={{ color: 'var(--brand-navy)' }}>{v.voucher_number}</td>
                    <td className="px-4 py-2.5" style={{ color: 'var(--navy-ink)' }}>{v.date}</td>
                    <td className="px-4 py-2.5 font-semibold tabular-nums" style={{ color: 'var(--navy-ink)' }}>
                      ₹{v.amount.toLocaleString('en-IN')}
                    </td>
                    <td className="px-4 py-2.5 max-w-[150px] truncate" style={{ color: 'var(--navy-ink)' }}>{v.paid_to}</td>
                    <td className="px-4 py-2.5 max-w-[200px] truncate" style={{ color: 'var(--ink-muted)' }}>{v.purpose}</td>
                    <td className="px-4 py-2.5">
                      <Badge variant="outline" className="text-[9px] px-1.5 py-0"
                        style={{ borderColor: catCfg.color + '40', color: catCfg.color }}>{catCfg.label}</Badge>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1">
                        <StatusIcon className="w-3 h-3" style={{ color: sc.color }} />
                        <span className="font-semibold" style={{ color: sc.color }}>{sc.label}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0"
                          onClick={() => setSelectedVoucher(v)} data-testid={`view-${v.id}`}>
                          <Eye className="w-3 h-3" style={{ color: 'var(--ink-muted)' }} />
                        </Button>
                        {v.status === 'pending_approval' && (v.created_by === user?.id || isAdmin) && (
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0"
                            onClick={() => handleDelete(v)} data-testid={`delete-${v.id}`}>
                            <Trash2 className="w-3 h-3 text-red-400" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {displayVouchers.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-12" style={{ color: 'var(--ink-muted)' }}>
                    {activeTab === 'pending' ? 'No pending vouchers' : 'No vouchers found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Detail Modal */}
      {selectedVoucher && (
        <VoucherDetailModal
          voucher={selectedVoucher}
          onClose={() => setSelectedVoucher(null)}
          onUpdated={handleUpdated}
          canApprove={isAdmin}
        />
      )}
    </div>
  );
}
